import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import './TestDetails.css'
import LoadingScreen from '../components/LoadingScreen'

const TestDetails = () => {
  const { attemptId } = useParams()
  const history = useHistory()
  const [attempt, setAttempt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchAttempt()
  }, [attemptId])

  const fetchAttempt = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        history.push('/login')
        return
      }

      const response = await fetch(`/api/student/attempts/${attemptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAttempt(data.attempt)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('API Error:', errorData)
        setError(errorData.error || errorData.message || `Failed to load attempt details (${response.status})`)
      }
    } catch (err) {
      console.error('Network Error:', err)
      setError(`Network error: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingScreen message="Loading Test Details" />
  }

  if (error || !attempt) {
    return (
      <div className="test-details-container">
        <header className="page-header">
          <h1>Test Details</h1>
          <button 
            className="back-button"
            onClick={() => history.push('/student-dashboard')}
          >
            ← Back to Dashboard
          </button>
        </header>
        <main className="test-details-content">
          <div className="error-card">
            <div className="error-icon">⚠️</div>
            <h2>Unable to Load Test Details</h2>
            <p className="error-message">{error || 'Attempt not found'}</p>
            <p className="error-hint">Attempt ID: {attemptId}</p>
            <button 
              className="primary-button"
              onClick={() => history.push('/student-dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    )
  }

  const isPassed = attempt.percentage >= (attempt.test?.passing_score || 0)
  const scoreColor = isPassed ? '#38a169' : '#e53e3e'
  const statusText = isPassed ? 'PASSED' : 'FAILED'

  return (
    <div className="test-details-container">
      <header className="page-header">
        <h1>Test Details</h1>
        <button 
          className="back-button"
          onClick={() => history.push('/student-dashboard')}
        >
          ← Back to Dashboard
        </button>
      </header>

      <main className="test-details-content">
        {attempt.test && (
          <div className="test-info-card">
            <h2>{attempt.test.title}</h2>
            <p className="test-description">{attempt.test.description}</p>
            
            <div className="score-section">
              <div className="score-circle" style={{ borderColor: scoreColor }}>
                <div className="score-value" style={{ color: scoreColor }}>
                  {attempt.percentage}%
                </div>
                <div className="score-label">FINAL SCORE</div>
              </div>
              
              <div className="score-details">
                <div className="score-detail-item">
                  <span className="detail-label">Points Earned</span>
                  <span className="detail-value">{attempt.score || 0}</span>
                </div>
                <div className="score-detail-item">
                  <span className="detail-label">Total Points</span>
                  <span className="detail-value">{attempt.test.total_points || 0}</span>
                </div>
                <div className="score-detail-item">
                  <span className="detail-label">Passing Score</span>
                  <span className="detail-value">{attempt.test.passing_score}%</span>
                </div>
                <div className="score-detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge status-${statusText.toLowerCase()}`}>
                    {statusText}
                  </span>
                </div>
                {attempt.submitted_at && (
                  <div className="score-detail-item">
                    <span className="detail-label">Submitted At</span>
                    <span className="detail-value">
                      {new Date(attempt.submitted_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {attempt.violations && attempt.violations.length > 0 && (
          <div className="violations-section">
            <h2>Violations Detected</h2>
            <div className="violations-list">
              {attempt.violations.map((violation, index) => (
                <div key={index} className="violation-item">
                  <span className="violation-icon">⚠️</span>
                  <div className="violation-content">
                    <h4>{violation.type.replace(/_/g, ' ')}</h4>
                    <p>{violation.message}</p>
                    <span className="violation-time">
                      {new Date(violation.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {attempt.questions && attempt.questions.length > 0 && (
          <div className="questions-section">
            <h2>Questions & Answers</h2>
            {attempt.questions.map((question, index) => {
              const userAnswer = question.user_answer
              const correctAnswerIndex = question.correct_answer
              // Use loose equality to handle potential string/number mismatch
              const isCorrect = userAnswer == correctAnswerIndex
              const userAnswerText = userAnswer !== null && userAnswer !== undefined 
                ? question.options[userAnswer] 
                : 'Not answered'
              const correctAnswerText = question.options[correctAnswerIndex]
              
              return (
                <div key={question.id} className={`question-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="question-header">
                    <h3>Question {index + 1}</h3>
                    <div className="question-points">
                      {question.points} {question.points === 1 ? 'point' : 'points'}
                      {isCorrect && <span className="correct-badge">✓ Correct</span>}
                      {!isCorrect && <span className="incorrect-badge">✗ Incorrect</span>}
                    </div>
                  </div>
                  
                  <div className="question-text">{question.question_text}</div>
                  
                  <div className="options-list">
                    {question.options.map((option, optIndex) => (
                      <div 
                        key={optIndex} 
                        className={`option-item ${
                          optIndex === correctAnswerIndex ? 'correct-answer' : ''
                        } ${
                          optIndex === userAnswer ? 'user-answer' : ''
                        }`}
                      >
                        <span className="option-label">
                          {String.fromCharCode(65 + optIndex)}.
                        </span>
                        <span className="option-text">{option}</span>
                        {optIndex === correctAnswerIndex && (
                          <span className="correct-indicator">✓ Correct Answer</span>
                        )}
                        {optIndex === userAnswer && optIndex !== correctAnswerIndex && (
                          <span className="user-indicator">Your Answer</span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="answer-summary">
                    <div className={`summary-item ${isCorrect ? 'summary-correct' : 'summary-incorrect'}`}>
                      <strong>Your Answer:</strong> {userAnswerText}
                    </div>
                    <div className="summary-item summary-correct">
                      <strong>Correct Answer:</strong> {correctAnswerText}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

export default TestDetails

